import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useUI } from "@/context/UIContext";
import { useNewAlertsCount } from "@/hooks/useNewAlertsCount";
import { NAV_ITEMS } from "./navConfig";
import { PanelLeftClose, PanelLeft, LifeBuoy, Radar } from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

function Brand({ collapsed }) {
  return (
    <div className={cn("flex items-center gap-2.5 px-4", collapsed && "justify-center px-0")}>
      <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-primary shadow-lg shadow-primary/30">
        <Radar className="h-5 w-5 text-primary-foreground" />
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-background" />
      </div>
      {!collapsed && (
        <div className="leading-tight">
          <div className="font-display text-[15px] font-semibold tracking-tight">ARC Beacon</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Kamar Technology</div>
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const { collapsed, toggleSidebar } = useUI();
  const newAlertsCount = useNewAlertsCount();

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "sticky top-0 hidden h-screen shrink-0 flex-col border-r border-border/70 bg-surface/80 backdrop-blur-xl transition-[width] duration-300 ease-out lg:flex",
        collapsed ? "w-[76px]" : "w-[248px]"
      )}
    >
      <div className={cn("flex h-16 items-center border-b border-border/60", collapsed && "justify-center")}>
        <Brand collapsed={collapsed} />
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3 no-scrollbar">
        <TooltipProvider delayDuration={0}>
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const badge = item.to === "/alerts" ? newAlertsCount : item.badge;
            const link = (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
                    collapsed && "justify-center px-0",
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary" />
                    )}
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                    {!collapsed && badge > 0 && (
                      <span className="ml-auto rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                        {badge}
                      </span>
                    )}
                    {collapsed && badge > 0 && (
                      <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
                    )}
                  </>
                )}
              </NavLink>
            );
            return collapsed ? (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>{link}</TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : link;
          })}
        </TooltipProvider>
      </nav>

      <div className="space-y-1 border-t border-border/60 p-3">
        <NavLink
          to="/help"
          className={cn(
            "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-secondary/50 hover:text-foreground",
            collapsed && "justify-center px-0"
          )}
          data-testid="nav-help"
        >
          <LifeBuoy className="h-[18px] w-[18px]" />
          {!collapsed && <span>Help &amp; Support</span>}
        </NavLink>
        <button
          onClick={toggleSidebar}
          data-testid="sidebar-toggle"
          className={cn(
            "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition hover:bg-secondary/50 hover:text-foreground",
            collapsed && "justify-center px-0"
          )}
        >
          {collapsed ? <PanelLeft className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
